import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Lock, Mail, User, Check, Flame, Loader2, X,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GOALS = ["Build Muscle & Mass", "Lose Fat & Get Lean", "Strength & Performance", "Overall Health & Discipline"];
const LEVELS = ["Beginner", "Intermediate", "Advanced"];
const DAYS = ["2-3 Days / Week", "4-5 Days / Week", "6+ Days / Week"];
const COMMITMENTS = ["I'm exploring", "I'm ready to commit", "I'm all in — change my life"];

const STEPS = [
  { key: "auth", label: "Account" },
  { key: "intake", label: "Application" },
  { key: "checkout", label: "Enroll" },
];

export default function OnboardingFunnel({ user, onAuthSuccess, onExit }) {
  const [step, setStep] = useState("auth");
  const [routing, setRouting] = useState(true);

  // Auth
  const [authMode, setAuthMode] = useState("signup");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  // Intake
  const [form, setForm] = useState({
    primary_goal: "", experience_level: "", training_days: "",
    deep_why: "", obstacles: "", commitment: "",
  });
  const [intakeLoading, setIntakeLoading] = useState(false);

  // Checkout
  const [packages, setPackages] = useState([]);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const fetchPackages = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/checkout/packages`);
      setPackages(data);
      if (data.length) setSelectedPkg(data[1]?.id || data[0].id);
    } catch (e) {
      console.error("Failed to load packages", e);
    }
  };

  useEffect(() => {
    const route = async () => {
      setRouting(true);
      if (!user) {
        setStep("auth");
        setRouting(false);
        return;
      }
      try {
        const { data } = await axios.get(`${API_URL}/api/intake/me`, { withCredentials: true });
        if (data.has_submitted) {
          await fetchPackages();
          setStep("checkout");
        } else {
          setStep("intake");
        }
      } catch {
        setStep("intake");
      } finally {
        setRouting(false);
      }
    };
    route();
  }, [user]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError("");
    setAuthLoading(true);
    const endpoint = authMode === "login" ? "/api/auth/login" : "/api/auth/register";
    const payload = authMode === "login" ? { email, password } : { email, password, name };
    try {
      const { data } = await axios.post(`${API_URL}${endpoint}`, payload, { withCredentials: true });
      onAuthSuccess(data);
      toast.success(`Welcome, ${data.name}. Let's build.`);
    } catch (err) {
      const d = err.response?.data?.detail;
      setAuthError(typeof d === "string" ? d : "Authentication failed. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleIntake = async (e) => {
    e.preventDefault();
    if (!form.primary_goal || !form.experience_level || !form.training_days || !form.deep_why || !form.commitment) {
      toast.error("Please complete all required fields.");
      return;
    }
    setIntakeLoading(true);
    try {
      await axios.post(`${API_URL}/api/intake`, form, { withCredentials: true });
      toast.success("Application received. Now choose your path.");
      await fetchPackages();
      setStep("checkout");
    } catch {
      toast.error("Submission failed. Please try again.");
    } finally {
      setIntakeLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!selectedPkg) {
      toast.error("Select a transformation package.");
      return;
    }
    setCheckoutLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/checkout/session`,
        { package_id: selectedPkg, origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setCheckoutLoading(false);
    }
  };

  const currentIndex = STEPS.findIndex((s) => s.key === step);

  return (
    <div className="min-h-screen bg-black text-white font-manrope" data-testid="onboarding-funnel">
      {/* Top bar */}
      <div className="border-b border-neutral-800 bg-neutral-950/40">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <img
            src="/assets/ssa-logo-white.png"
            alt="ForgedByStokes"
            className="h-11 w-auto"
          />
          <button
            onClick={onExit}
            className="text-neutral-500 hover:text-white transition-colors flex items-center gap-1.5 text-xs uppercase tracking-widest"
            data-testid="funnel-exit-button"
          >
            <X className="w-4 h-4" /> Exit
          </button>
        </div>
      </div>

      {/* Stepper */}
      <div className="max-w-5xl mx-auto px-6 pt-10">
        <div className="flex items-center justify-center gap-3 sm:gap-6">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex items-center gap-3 sm:gap-6">
              <div className="flex items-center gap-2">
                <div
                  className={`w-8 h-8 flex items-center justify-center border text-xs font-bebas tracking-wider ${
                    i < currentIndex
                      ? "bg-white text-black border-white"
                      : i === currentIndex
                      ? "border-white text-white"
                      : "border-neutral-800 text-neutral-600"
                  }`}
                >
                  {i < currentIndex ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                <span className={`text-[10px] uppercase tracking-widest font-semibold hidden sm:block ${
                  i <= currentIndex ? "text-white" : "text-neutral-600"
                }`}>
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`w-8 sm:w-16 h-px ${i < currentIndex ? "bg-white" : "bg-neutral-800"}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        {routing && (
          <div className="flex justify-center py-24" data-testid="funnel-loading">
            <Loader2 className="w-8 h-8 animate-spin text-neutral-600" />
          </div>
        )}

        {/* STEP 1: AUTH */}
        {!routing && step === "auth" && (
          <div className="max-w-md mx-auto" data-testid="funnel-auth-step">
            <div className="text-center mb-8 space-y-2">
              <h1 className="font-bebas text-4xl sm:text-5xl uppercase tracking-wide leading-none">
                {authMode === "signup" ? "Create Your Account" : "Welcome Back"}
              </h1>
              <p className="text-xs text-neutral-400 uppercase tracking-widest">
                Step one of your covenant
              </p>
            </div>

            {authError && (
              <div className="mb-4 bg-white text-black px-4 py-3 text-xs uppercase tracking-wider font-semibold" data-testid="funnel-auth-error">
                {authError}
              </div>
            )}

            <form onSubmit={handleAuth} className="space-y-5 border border-neutral-800 bg-neutral-950/40 p-8">
              {authMode === "signup" && (
                <div className="space-y-1">
                  <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Full Name</label>
                  <div className="relative">
                    <User className="w-4 h-4 text-neutral-500 absolute left-0 bottom-2" />
                    <input
                      type="text" required placeholder="Elijah Shepherd" value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full bg-transparent border-b border-neutral-800 py-2 pl-7 text-sm text-white rounded-none placeholder-neutral-700 focus:border-white focus:outline-none"
                      data-testid="funnel-name-input"
                    />
                  </div>
                </div>
              )}
              <div className="space-y-1">
                <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Email Address</label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-neutral-500 absolute left-0 bottom-2" />
                  <input
                    type="email" required placeholder="steward@temple.com" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-transparent border-b border-neutral-800 py-2 pl-7 text-sm text-white rounded-none placeholder-neutral-700 focus:border-white focus:outline-none"
                    data-testid="funnel-email-input"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Password</label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-neutral-500 absolute left-0 bottom-2" />
                  <input
                    type="password" required placeholder="••••••••" value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-transparent border-b border-neutral-800 py-2 pl-7 text-sm text-white rounded-none placeholder-neutral-700 focus:border-white focus:outline-none"
                    data-testid="funnel-password-input"
                  />
                </div>
              </div>
              <button
                type="submit" disabled={authLoading}
                className="w-full bg-white text-black py-3 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="funnel-auth-submit"
              >
                {authLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>
                  <span>{authMode === "signup" ? "Create Account & Continue" : "Sign In & Continue"}</span>
                  <ArrowRight className="w-4 h-4" />
                </>}
              </button>
            </form>

            <p className="mt-6 text-center text-[11px] text-neutral-500">
              {authMode === "signup" ? "Already have an account? " : "New to ForgedByStokes? "}
              <button
                onClick={() => { setAuthMode(authMode === "signup" ? "login" : "signup"); setAuthError(""); }}
                className="text-white underline font-semibold"
                data-testid="funnel-auth-toggle"
              >
                {authMode === "signup" ? "Sign In" : "Create Account"}
              </button>
            </p>
          </div>
        )}

        {/* STEP 2: INTAKE */}
        {!routing && step === "intake" && (
          <div className="max-w-2xl mx-auto" data-testid="funnel-intake-step">
            <div className="text-center mb-10 space-y-2">
              <h1 className="font-bebas text-4xl sm:text-5xl uppercase tracking-wide leading-none">Apply To Train</h1>
              <p className="text-xs text-neutral-400 uppercase tracking-widest max-w-md mx-auto leading-relaxed">
                Tell Stokes who you are and why you fight. This shapes your transformation.
              </p>
            </div>

            <form onSubmit={handleIntake} className="space-y-8 border border-neutral-800 bg-neutral-950/40 p-8">
              {/* Primary goal */}
              <div className="space-y-3">
                <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Primary Goal *</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {GOALS.map((g) => (
                    <button
                      type="button" key={g}
                      onClick={() => setForm({ ...form, primary_goal: g })}
                      className={`text-left px-4 py-3 border text-xs uppercase tracking-wider font-semibold transition-all ${
                        form.primary_goal === g ? "bg-white text-black border-white" : "border-neutral-800 text-neutral-400 hover:border-neutral-600"
                      }`}
                      data-testid={`intake-goal-${g.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>

              {/* Experience */}
              <div className="space-y-3">
                <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Experience Level *</label>
                <div className="flex flex-wrap gap-2">
                  {LEVELS.map((l) => (
                    <button
                      type="button" key={l}
                      onClick={() => setForm({ ...form, experience_level: l })}
                      className={`px-4 py-2 border text-xs uppercase tracking-wider font-semibold transition-all ${
                        form.experience_level === l ? "bg-white text-black border-white" : "border-neutral-800 text-neutral-400 hover:border-neutral-600"
                      }`}
                      data-testid={`intake-level-${l.toLowerCase()}`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Training days */}
              <div className="space-y-3">
                <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Training Availability *</label>
                <div className="flex flex-wrap gap-2">
                  {DAYS.map((d) => (
                    <button
                      type="button" key={d}
                      onClick={() => setForm({ ...form, training_days: d })}
                      className={`px-4 py-2 border text-xs uppercase tracking-wider font-semibold transition-all ${
                        form.training_days === d ? "bg-white text-black border-white" : "border-neutral-800 text-neutral-400 hover:border-neutral-600"
                      }`}
                      data-testid={`intake-days-${d.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()}`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>

              {/* Deep Why */}
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Your Deep Why *</label>
                <p className="text-[11px] text-neutral-600 leading-relaxed">What truly drives you? Family, future health, a calling, a promise you made. Be honest.</p>
                <textarea
                  rows={4} required value={form.deep_why}
                  onChange={(e) => setForm({ ...form, deep_why: e.target.value })}
                  placeholder="I want to be strong enough to carry my family through anything, and to honor the temple God gave me..."
                  className="w-full bg-transparent border border-neutral-800 p-3 text-sm text-white rounded-none placeholder-neutral-700 focus:border-white focus:outline-none resize-none"
                  data-testid="intake-deep-why"
                />
              </div>

              {/* Obstacles */}
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Biggest Obstacle (optional)</label>
                <textarea
                  rows={2} value={form.obstacles}
                  onChange={(e) => setForm({ ...form, obstacles: e.target.value })}
                  placeholder="Time, consistency, past injuries, motivation..."
                  className="w-full bg-transparent border border-neutral-800 p-3 text-sm text-white rounded-none placeholder-neutral-700 focus:border-white focus:outline-none resize-none"
                  data-testid="intake-obstacles"
                />
              </div>

              {/* Commitment */}
              <div className="space-y-3">
                <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Commitment Level *</label>
                <div className="flex flex-col gap-2">
                  {COMMITMENTS.map((c) => (
                    <button
                      type="button" key={c}
                      onClick={() => setForm({ ...form, commitment: c })}
                      className={`text-left px-4 py-3 border text-xs uppercase tracking-wider font-semibold transition-all ${
                        form.commitment === c ? "bg-white text-black border-white" : "border-neutral-800 text-neutral-400 hover:border-neutral-600"
                      }`}
                      data-testid={`intake-commitment-${c.replace(/[^a-zA-Z]/g, "").toLowerCase().slice(0, 12)}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="submit" disabled={intakeLoading}
                className="w-full bg-white text-black py-3 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="intake-submit-button"
              >
                {intakeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>
                  <span>Submit Application & Choose Program</span>
                  <ArrowRight className="w-4 h-4" />
                </>}
              </button>
            </form>
          </div>
        )}

        {/* STEP 3: CHECKOUT */}
        {!routing && step === "checkout" && (
          <div data-testid="funnel-checkout-step">
            <div className="text-center mb-10 space-y-2">
              <h1 className="font-bebas text-4xl sm:text-5xl uppercase tracking-wide leading-none">Choose Your Transformation</h1>
              <p className="text-xs text-neutral-400 uppercase tracking-widest max-w-md mx-auto leading-relaxed">
                Your application is approved. Secure your spot and begin the work.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {packages.map((pkg) => {
                const isSelected = selectedPkg === pkg.id;
                const isFeatured = pkg.id === "transformation";
                return (
                  <button
                    type="button" key={pkg.id}
                    onClick={() => setSelectedPkg(pkg.id)}
                    className={`text-left border p-6 flex flex-col transition-all relative ${
                      isSelected ? "border-white bg-neutral-950" : "border-neutral-800 bg-neutral-950/20 hover:border-neutral-600"
                    }`}
                    data-testid={`package-card-${pkg.id}`}
                  >
                    {isFeatured && (
                      <span className="absolute -top-3 left-6 bg-white text-black text-[9px] uppercase font-bold tracking-widest px-2 py-0.5">
                        Most Chosen
                      </span>
                    )}
                    <div className={`w-5 h-5 border flex items-center justify-center mb-4 ${isSelected ? "bg-white border-white" : "border-neutral-700"}`}>
                      {isSelected && <Check className="w-3.5 h-3.5 text-black" />}
                    </div>
                    <span className="text-[10px] uppercase font-mono text-neutral-500 tracking-widest">{pkg.duration}</span>
                    <h3 className="font-bebas text-2xl uppercase tracking-wide leading-none mt-1">{pkg.name}</h3>
                    <div className="flex items-baseline gap-1 my-3">
                      <span className="font-bebas text-4xl">${pkg.amount}</span>
                      <span className="text-[10px] uppercase text-neutral-500 tracking-widest">one-time</span>
                    </div>
                    <p className="text-xs text-neutral-400 leading-relaxed mb-4">{pkg.tagline}</p>
                    <ul className="space-y-2 mt-auto">
                      {pkg.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-[11px] text-neutral-400 leading-snug">
                          <Check className="w-3.5 h-3.5 text-white shrink-0 mt-0.5" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </button>
                );
              })}
            </div>

            <div className="max-w-md mx-auto mt-10 space-y-4">
              <button
                onClick={handleCheckout} disabled={checkoutLoading || !selectedPkg}
                className="w-full bg-white text-black py-4 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="checkout-proceed-button"
              >
                {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>
                  <Lock className="w-4 h-4" />
                  <span>Proceed To Secure Checkout</span>
                </>}
              </button>
              <p className="text-center text-[10px] text-neutral-600 uppercase tracking-widest flex items-center justify-center gap-1.5">
                <Lock className="w-3 h-3" /> Secured by Stripe • SSL Encrypted
              </p>
              <button
                onClick={() => setStep("intake")}
                className="w-full text-neutral-500 hover:text-white text-[11px] uppercase tracking-widest flex items-center justify-center gap-1.5 transition-colors"
                data-testid="checkout-back-button"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Edit My Application
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
